/**
 * Level-2 crafter storefronts — deterministic rotating credit offers (§5).
 *
 * Rotations are keyed to a fixed epoch and building id: relogging cannot
 * reroll them. Offers are bounded one-copy artisan stock, priced above resale
 * so buying is a convenience, never an arbitrage. The base-value table here is
 * ALSO the anchor for Guild payouts (§6.2) — one authority, no drift.
 */
import { hashSeed } from "../rng.js";
import { SETTLEMENT_QUALITY_TIERS, type SettlementQuality } from "./model.js";
import type { SettlementFacilityV1, SettlementStateV1 } from "./production.js";
import { settlementFacilityFamily, settlementFacilityVariant } from "./production.js";

export const SETTLEMENT_STOREFRONT_INTERVAL_MS = 24 * 3_600_000;
export const SETTLEMENT_STOREFRONT_SLOTS = 4;
/** Buy price = base value × quality × this premium. Resale never exceeds base
 * value, so the storefront can never be an arbitrage loop. */
export const STOREFRONT_PREMIUM_PERMILLE = 2000;

/** Authoritative base values in credits (v1, provisional tuning data). Ingots
 * derive from their parts so the table cannot drift from the catalog. */
export const SETTLEMENT_BASE_VALUES: Readonly<Record<string, number>> = {
  wheat: 2, bread: 3, "hearty-meal": 5,
  "materia-red": 6, "materia-blue": 6, "materia-yellow": 6,
  "materia-purple": 14, "materia-green": 14, "materia-orange": 14,
  "ore-ferrite": 4, "ore-adamant": 6, "ore-runite": 8,
  "scrap-alloy": 5, ferriron: 9, "ore-cryonite": 6, "ore-volatite": 5, "exotic-crystal": 25,
  "fuel-cell": 10, medkit: 12, "shield-pack": 18,
  "ammo-light": 1, "ammo-heavy": 1,
  "rifle-mk1": 120, "rifle-longshot": 180, "rifle-vulcan": 140,
  "minigun-tempest": 260, "launcher-ares": 300,
  "upgrade-package-2": 60, "upgrade-package-3": 140,
  "module-mining": 160, "module-boost": 150, "module-pulse": 150,
  "keycard-hover-bike": 400, "keycard-hauler-speeder": 600,
  "gear-drifter-torso": 170, "gear-drifter-head": 70, "gear-drifter-arms": 120,
  "gear-drifter-legs": 120, "gear-drifter-feet": 60,
};

const INGOT_WORK_VALUE = 4;

export function settlementBaseValue(id: string): number {
  const flat = SETTLEMENT_BASE_VALUES[id];
  if (flat !== undefined) return flat;
  const ingot = /^ingot-([a-z]+)-([a-z]+)$/.exec(id);
  if (ingot) {
    return (SETTLEMENT_BASE_VALUES[`materia-${ingot[1]}`] ?? 0)
      + (SETTLEMENT_BASE_VALUES[`ore-${ingot[2]}`] ?? 0) + INGOT_WORK_VALUE;
  }
  return 0;
}

/** Q1..Q5 price multipliers, permille. Quality raises value steeply so Q4–Q5
 * production and orders stay worth protecting. */
export const QUALITY_VALUE_PERMILLE: readonly number[] = [800, 1000, 1500, 2500, 4000];

export function settlementItemValue(id: string, quality: SettlementQuality, n = 1): number {
  const index = SETTLEMENT_QUALITY_TIERS.indexOf(quality);
  return Math.ceil(settlementBaseValue(id) * n * (QUALITY_VALUE_PERMILLE[index] ?? 1000) / 1000);
}

/** Per-variant artisan stock pools. A row is one rotating offer candidate. */
const STOREFRONT_STOCK: Readonly<Record<string, readonly { id: string; n: number }[]>> = {
  smithy: [
    { id: "rifle-mk1", n: 1 }, { id: "rifle-longshot", n: 1 },
    { id: "rifle-vulcan", n: 1 }, { id: "minigun-tempest", n: 1 },
    { id: "launcher-ares", n: 1 },
    { id: "ammo-heavy", n: 45 }, { id: "ammo-light", n: 120 },
  ],
  garage: [
    { id: "upgrade-package-2", n: 1 }, { id: "module-mining", n: 1 },
    { id: "module-boost", n: 1 }, { id: "fuel-cell", n: 3 },
  ],
  shipyard: [
    { id: "keycard-hover-bike", n: 1 }, { id: "keycard-hauler-speeder", n: 1 },
    { id: "fuel-cell", n: 5 }, { id: "module-boost", n: 1 },
  ],
  "outfit-bench": [
    { id: "gear-drifter-torso", n: 1 }, { id: "gear-drifter-head", n: 1 },
    { id: "gear-drifter-arms", n: 1 }, { id: "gear-drifter-legs", n: 1 },
  ],
};

export interface SettlementStorefrontOffer {
  id: string;
  facilityId: string;
  itemId: string;
  n: number;
  quality: SettlementQuality;
  price: number;
  /** Already bought this rotation (one-copy stock). */
  sold: boolean;
}

export function settlementStorefrontEpoch(nowMs: number): number {
  return Math.floor(Math.max(0, nowMs) / SETTLEMENT_STOREFRONT_INTERVAL_MS);
}

/** The deterministic offer wall for one building. Empty below Level 2 or for
 * non-crafter families. Purchases are matched by offer id, which embeds the
 * epoch — a stale buy after rotation simply no longer resolves. */
export function settlementStorefrontOffers(state: SettlementStateV1,
  facility: SettlementFacilityV1, nowMs: number): SettlementStorefrontOffer[] {
  if (settlementFacilityFamily(facility) !== "crafter" || facility.grade < 2) return [];
  const stock = STOREFRONT_STOCK[settlementFacilityVariant(facility)];
  if (!stock || stock.length === 0) return [];
  const epoch = settlementStorefrontEpoch(nowMs);
  const sold = new Set(state.storefrontPurchases ?? []);
  const offers: SettlementStorefrontOffer[] = [];
  for (let slot = 0; slot < SETTLEMENT_STOREFRONT_SLOTS; slot++) {
    const row = stock[hashSeed(`${state.beaconId}:${facility.id}:store:${epoch}:${slot}`) % stock.length]!;
    const quality = SETTLEMENT_QUALITY_TIERS[
      1 + hashSeed(`${state.beaconId}:${facility.id}:store:${epoch}:${slot}:q`) % 3]!;
    const id = `store:${facility.id}:${epoch}:${slot}`;
    offers.push({
      id,
      facilityId: facility.id,
      itemId: row.id,
      n: row.n,
      quality,
      price: Math.ceil(settlementItemValue(row.id, quality, row.n) * STOREFRONT_PREMIUM_PERMILLE / 1000),
      sold: sold.has(id),
    });
  }
  return offers;
}

/** Keep only this rotation's receipts. The epoch is the second-to-last id
 * segment, so facility pids can never collide with it. */
export function pruneStorefrontPurchases(purchases: readonly string[] | undefined,
  nowMs: number): string[] {
  const epoch = String(settlementStorefrontEpoch(nowMs));
  return (purchases ?? []).filter((id) => {
    const parts = id.split(":");
    return parts[parts.length - 2] === epoch;
  }).slice(0, 64);
}
